import React, { useContext, useEffect, useState } from "react"
import { Alert, StyleSheet, Text, TouchableOpacity, View } from "react-native"
import Loader from "./utility/loader"
import { ThemeContext } from "./utility/context/ThemeContext"
import api from "./api-client"
import { authSlice } from "./auth-slice"
import { IImpactArea, PopulatedValue } from "@ecountabl/lib"
import { CustomActionSheetScrollView } from "./utility/CustomActionSheet"
import { ThemedButton } from "./utility/input/buttons"
import { MAX_CONTENT_WIDTH } from "./config"
import { useAppDispatch, useAppSelector } from './store'
import { textStyles } from "./style"
import Bullet from "./style/bullet"
import Typewriter from "./utility/typewriter"
import Haptics from "./utility/haptics"
import Analytics from "./utility/analytics"
import { SelectedIcon } from "./utility/selected-icon"
import AccordionListItem from "./accordion-list-item"
import { ThemedIconSwitch } from "./utility/input/misc/themed-icon-switch"

type Props = {
  hideSheet: () => void
  onboarding?: boolean
}

const DEFAULT_FORCED_SELECTION_TEXT = "Forced selection. Cannot be undone"

const SpecifyValues:React.FC<Props> = ({ hideSheet, onboarding=false }: Props) => {
  const theme = useContext(ThemeContext)
  const { danger, textMain, disabled: disabledColor, textAlt, bgSecond } = theme.theme.colors
  const { error, status: valuesStatus, map: valuesState } = useAppSelector(state => state.values)
  const valuesLoading = valuesStatus === 'idle' || valuesStatus === 'loading'
  const { user } = useAppSelector(state => state.auth)
  const _iaMap = user?.valuedImpactAreaMap
  const customer = user?.customer
  const [values, setValues] = useState({
    values: [],
    valuedImpactAreaMap: _iaMap || {}
  })
  const [expandedBySlug, setExpandedBySlug] = useState<Record<string, boolean>>({})

  const [loading, setLoading] = useState(false)
  // if onboarding, any values are changes to be saved
  const [hasChanges, setHasChanges] = useState(onboarding)

  const dispatch = useAppDispatch()

  // Compare current state with initial props to detect changes
  useEffect(() => {
    if (!onboarding) {
      setHasChanges(
        JSON.stringify(values.valuedImpactAreaMap, null, 2) !== JSON.stringify(_iaMap, null, 2)
      )
    }
  }, [values.valuedImpactAreaMap, _iaMap, onboarding])

  const save = () => {
    setLoading(true)
    let succeeded = false
    const hasValuedImpactAreaMap = Object.values(values.valuedImpactAreaMap || {}).some(Boolean)
    Analytics.track('user_values_changed', {
      has_valuedImpactAreaMap: hasValuedImpactAreaMap,
    })
    api.setUserValuedImpactAreas(values.valuedImpactAreaMap)
      .then(() => {
        dispatch(authSlice.actions.setUserField({ field: 'valuedImpactAreaMap', value: values.valuedImpactAreaMap }))
        dispatch(authSlice.actions.setUserField({ field: 'needsValueSelection', value: false }))
        succeeded = true
      })
      .catch((ex: Error) => {
        Alert.alert('Error', ex.message, [
          { text: 'OK', onPress: () => { /* noop */ } }
        ], {
          cancelable: false,
        })
      }).finally(() => {
        hideSheet()
        setLoading(false)
        Analytics.track('specify_values_submitted', {
          success: succeeded,
        })
      })
  }

  // handle setting up of values map and values
  useEffect(() => {
    const customerOrderRaw = customer?.config?.valuesSortOrder
    const customerOrder =
      customerOrderRaw && typeof customerOrderRaw === 'object' && !Array.isArray(customerOrderRaw)
        ? (customerOrderRaw as Record<string, unknown>)
        : undefined

    const rankForSlug = (slug: string | undefined): number => {
      if (!slug) return 0
      const v = customerOrder?.[slug]
      return typeof v === 'number' && Number.isFinite(v) ? v : 0
    }

    const _values = Object.values<PopulatedValue>(valuesState).sort((a, b) => {
      const aRank = rankForSlug(a?.slug)
      const bRank = rankForSlug(b?.slug)
      if (aRank !== bRank) return bRank - aRank

      const aSort = typeof (a as unknown as { sortOrder?: unknown })?.sortOrder === 'number'
        ? ((a as unknown as { sortOrder: number }).sortOrder)
        : 0
      const bSort = typeof (b as unknown as { sortOrder?: unknown })?.sortOrder === 'number'
        ? ((b as unknown as { sortOrder: number }).sortOrder)
        : 0
      if (aSort !== bSort) return bSort - aSort

      const aLabel = (a?.label || '').toString()
      const bLabel = (b?.label || '').toString()
      return aLabel.localeCompare(bLabel)
    })
    let map = _iaMap || {}
    _values.forEach(v => {
      if (v.forceSelection) {
        const forcedCaring = (v.impactAreas || []).reduce<Record<string, boolean>>((acc, ia) => {
          if (ia?._id) acc[ia._id] = true
          return acc
        }, {})
        map = { ...map, ...forcedCaring }
      }
    })

    setValues({
      values: _values,
      valuedImpactAreaMap: map
    })
  }, [valuesState, _iaMap, user, customer?.config?.valuesSortOrder])

  const setAllImpactAreasForValue = (value: PopulatedValue, nextSelected: boolean) => {
    const updates: Record<string, boolean> = {}
    const impactAreas = value.impactAreas || []
    impactAreas.forEach((ia: IImpactArea) => {
      if (ia?._id) updates[ia._id] = nextSelected
    })

    setValues({
      values: values.values,
      valuedImpactAreaMap: {
        ...values.valuedImpactAreaMap,
        ...updates
      }
    })
  }

  const onToggleValue = (value: PopulatedValue) => {
    Haptics.selection()
    if (value.forceSelection) return

    const anySelected = (value.impactAreas || []).some(
      (ia: IImpactArea) => !!values.valuedImpactAreaMap?.[ia._id]
    )
    setAllImpactAreasForValue(value, !anySelected)
  }

  if (valuesLoading) {
    return (
      <View style={{paddingHorizontal: 16, maxWidth: MAX_CONTENT_WIDTH, alignSelf: "center"}}>
        <Loader size="large" />
        <Text style={[textStyles.body]}>
          Fetching values to choose from <Typewriter text="..." restartDelay={500} style={textStyles.body}/>
        </Text>
      </View>
    )
  }

  const valueSlugs = (values.values || [])
    .map((v: PopulatedValue) => v?.slug)
    .filter((s): s is string => typeof s === 'string' && !!s)
  const anyExpanded = valueSlugs.some((s) => !!expandedBySlug?.[s])
  const allExpanded = valueSlugs.length > 0 && valueSlugs.every((s) => !!expandedBySlug?.[s])
  const canExpandAll = !allExpanded
  const canCollapseAll = anyExpanded

  return (
    <>
      <CustomActionSheetScrollView>
        {
          error ? <>
            <View style={{flexDirection: "row", alignItems: "center"}}>
              <Bullet color={danger} size={6} style={{paddingRight: 6}} />
              <Text style={[textStyles.body]}>
                {error}
              </Text>
            </View>
          </> : <View style={{gap: 10}}>
            <Text style={[textStyles.h2, {color: textMain}]}>Choose Your Values</Text>
            <Text style={[textStyles.body, {color: textMain}]}>
              Choose the values you care most about. Your choices enable us to
              help you find businesses that match your values. Change your values at any time.
            </Text>
            <Text style={[textStyles.label, {color: textAlt}]}>
              Tap the toggle to select/deselect a value. Tap the row to read more.
            </Text>
            <View style={styles.expandControlsRow}>
              <TouchableOpacity
                disabled={loading || !canExpandAll}
                onPress={() => {
                  const next: Record<string, boolean> = {}
                  values.values.forEach((v: PopulatedValue) => {
                    if (v?.slug) next[v.slug] = true
                  })
                  setExpandedBySlug(next)
                }}
                hitSlop={10}
              >
                <Text
                  style={[
                    textStyles.label,
                    canExpandAll ? { fontFamily: 'poppins-semi-bold' } : null,
                    { color: canExpandAll ? textMain : disabledColor }
                  ]}
                >
                  Expand all
                </Text>
              </TouchableOpacity>

              <Text style={[styles.expandControlsDot, {color: textAlt}]}>·</Text>

              <TouchableOpacity
                disabled={loading || !canCollapseAll}
                onPress={() => setExpandedBySlug({})}
                hitSlop={10}
              >
                <Text
                  style={[
                    textStyles.label,
                    canCollapseAll ? { fontFamily: 'poppins-semi-bold' } : null,
                    { color: canCollapseAll ? textMain : disabledColor }
                  ]}
                >
                  Collapse all
                </Text>
              </TouchableOpacity>
            </View>
            {
              values.values.map((value: PopulatedValue, i) => {
                const anySelected = (value.impactAreas || []).some(
                  (ia: IImpactArea) => !!values.valuedImpactAreaMap?.[ia._id]
                )
                const selected = value.forceSelection ? true : anySelected
                const isExpanded = !!expandedBySlug?.[value.slug]
                const valueColorRaw = (value as unknown as { color?: unknown })?.color
                const valueColor = typeof valueColorRaw === 'string' ? valueColorRaw : undefined
                const chevronIcon = isExpanded ? 'chevron-down' : 'chevron-forward'

                return (
                  <AccordionListItem
                    key={value.slug || i}
                    expanded={isExpanded}
                    wrapStyle={[
                      styles.valueCard,
                      {
                        backgroundColor: bgSecond
                      }
                    ]}
                    title={
                      <View style={styles.valueRow}>
                        <ThemedIconSwitch
                          accessibilityLabel={
                            typeof value.label === 'string' && value.label.trim().length > 0
                            ? `${value.label} value`
                            : 'value'
                          }
                          icon={value.icon}
                          color={valueColor}
                          selected={selected}
                          disabled={loading || value.forceSelection}
                          onPress={() => onToggleValue(value)}
                        />
                        <TouchableOpacity
                          style={styles.valueExpandPressable}
                          onPress={() => {
                            const slug = value.slug
                            setExpandedBySlug((prev) => ({ ...prev, [slug]: !prev?.[slug] }))
                          }}
                          disabled={loading}
                          hitSlop={10}
                        >
                          <View style={styles.valueLabelWrap}>
                            <Text
                              style={[textStyles.h4, styles.valueLabelText, { color: textMain }]}
                            >
                              {value.label}
                            </Text>
                          </View>
                          <View style={styles.valueControlsWrap}>
                            {value.forceSelection ? (
                              <View style={[styles.lockPill, { borderColor: textAlt }]}>
                                <SelectedIcon
                                  icon={{ family: 'Ionicons', name: 'lock-closed' }}
                                  color={textAlt}
                                  size={14}
                                />
                              </View>
                            ) : null}
                            <SelectedIcon
                              icon={{ family: 'Ionicons', name: chevronIcon }}
                              color={textAlt}
                              size={18}
                            />
                          </View>
                        </TouchableOpacity>
                      </View>
                    }
                  >
                    <View style={styles.valueExpanded}>
                      <Text style={[textStyles.body, { color: textAlt }]}>
                        {value.description}
                      </Text>
                      {value.forceSelection ? (
                        <View style={styles.forcedRow}>
                          <SelectedIcon
                            icon={{ family: 'Ionicons', name: 'lock-closed' }}
                            color={textAlt}
                            size={14}
                            style={styles.forcedLockIcon}
                          />
                          <Text
                            style={[textStyles.subscript, styles.forcedText, { color: textAlt }]}
                          >
                            {DEFAULT_FORCED_SELECTION_TEXT}
                          </Text>
                        </View>
                      ) : null}
                    </View>
                  </AccordionListItem>
                )
              })
            }
          </View>
        }
      </CustomActionSheetScrollView>
      {
        !valuesLoading && (
          <View style={styles.submitButtonWrapper}>
            <ThemedButton
              text={error || !hasChanges ? "Close" : "Save"}
              onPress={error || !hasChanges ? hideSheet : save}
              loading={loading}
              variant="primary"
              hapticLevel={
                error || !hasChanges ? 'Soft' : undefined
              }
            />
          </View>
        )
      }
    </>
  )
}

export default SpecifyValues

const styles = StyleSheet.create({
  expandControlsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 12
  },
  expandControlsDot: {
    fontSize: 14,
    paddingHorizontal: 6
  },
  valueCard: {
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderRadius: 10,
    width: '100%',
    alignSelf: 'stretch',
  },
  valueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
  },
  valueExpandPressable: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minWidth: 0,
    paddingLeft: 10,
  },
  valueLabelWrap: {
    flex: 1,
    minWidth: 0,
    paddingRight: 10,
  },
  valueLabelText: {
    flexShrink: 1,
  },
  valueControlsWrap: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  lockPill: {
    width: 28,
    height: 22,
    borderRadius: 999,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8
  },
  valueIconWrap: {
    height: 35,
    width: 35,
    borderRadius: 5,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1
  },
  valueExpanded: {
    width: "100%",
    minWidth: 0,
    paddingTop: 8,
  },
  forcedRow: {
    width: "100%",
    minWidth: 0,
    marginTop: 8,
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 6,
  },
  forcedLockIcon: {
    width: 14,
    height: 14,
    marginTop: 2,
    flexShrink: 0,
  },
  forcedText: {
    flex: 1,
    minWidth: 0,
    flexShrink: 1,
  },
  submitButtonWrapper: {
    paddingHorizontal: 16,
    paddingTop: 16
  }
})